import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useUser } from "@/hooks/useUser";
import type { User, UserShort } from "@/lib/humhub-api/types";

export interface FallbackUserSource {
  id: number;
  guid?: string;
  display_name: string;
  image_url?: string;
}

export interface SendMessageOpenOptions {
  source?: "popup" | "default";
  initialSearchMode?: "keyword" | "participants";
  prefillTargetParticipant?: boolean;
}

export const useSendMessageToUser = () => {
  const navigate = useNavigate();
  const { fetchUserById } = useUser();

  // モーダル関連のstate
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isCreateChatModalOpen, setIsCreateChatModalOpen] = useState(false);
  const [selectedUserForChat, setSelectedUserForChat] = useState<
    User | undefined
  >(undefined);
  const [createModalUser, setCreateModalUser] = useState<User | undefined>(
    undefined,
  );
  const [searchModalInitialMode, setSearchModalInitialMode] = useState<
    "keyword" | "participants" | undefined
  >(undefined);
  const [searchModalInitialParticipants, setSearchModalInitialParticipants] =
    useState<UserShort[] | undefined>(undefined);
  const [isUserLoading, setIsUserLoading] = useState(false);

  // フォールバックユーザー情報を統一フォーマットに変換（メモ化）
  const createFallbackUser = useCallback((source: FallbackUserSource): User => {
    return {
      id: source.id,
      guid: source.guid || "",
      display_name: source.display_name,
      image_url: source.image_url || "",
      profile: {
        firstname: source.display_name.split(" ")[0] || "",
        lastname: source.display_name.split(" ").slice(1).join(" ") || "",
      },
    } as User;
  }, []);

  const toUserShort = useCallback((user: User): UserShort => {
    return {
      id: user.id,
      guid: user.guid || String(user.id),
      display_name: user.display_name,
      url: user.url || "",
      image_url: user.image_url || "",
    };
  }, []);

  const applySearchModalOpenOptions = useCallback(
    (targetUser: User, openOptions?: SendMessageOpenOptions) => {
      setSearchModalInitialMode(openOptions?.initialSearchMode);

      if (openOptions?.prefillTargetParticipant) {
        setSearchModalInitialParticipants([toUserShort(targetUser)]);
      } else {
        setSearchModalInitialParticipants(undefined);
      }
    },
    [toUserShort],
  );

  // メッセージ送信処理（統一版）（メモ化）
  const handleSendMessage = useCallback(
    async (
      userId: number,
      fallbackSource?: FallbackUserSource,
      openOptions?: SendMessageOpenOptions,
    ) => {
      try {
        setIsUserLoading(true);

        // fetchUserByIdで完全なUserデータを取得
        const fullUserData = await fetchUserById(userId);
        setSelectedUserForChat(fullUserData);
        applySearchModalOpenOptions(fullUserData, openOptions);
        setIsSearchModalOpen(true);
      } catch (error) {
        console.error("Failed to fetch user data:", error);

        // フォールバック処理
        if (fallbackSource) {
          const fallbackUser = createFallbackUser(fallbackSource);
          setSelectedUserForChat(fallbackUser);
          applySearchModalOpenOptions(fallbackUser, openOptions);
          setIsSearchModalOpen(true);
        } else {
          console.error("No fallback data available for user:", userId);
        }
      } finally {
        setIsUserLoading(false);
      }
    },
    [fetchUserById, createFallbackUser, applySearchModalOpenOptions],
  );

  // チャットルーム選択ハンドラ（メモ化）
  const handleChatRoomSelect = useCallback(
    (roomId: number | undefined) => {
      if (roomId) {
        navigate({
          to: "/chat-room/$chat-room-id",
          params: { "chat-room-id": roomId.toString() },
        });
      }
    },
    [navigate],
  );

  // 新規チャット作成ハンドラ（メモ化）
  const handleCreateNewChat = useCallback((selectedUser?: User) => {
    setIsSearchModalOpen(false);
    setCreateModalUser(selectedUser);
    setIsCreateChatModalOpen(true);
  }, []);

  // モーダルクローズハンドラ（メモ化）
  const handleCloseSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
    setSelectedUserForChat(undefined);
    setSearchModalInitialMode(undefined);
    setSearchModalInitialParticipants(undefined);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateChatModalOpen(false);
    setCreateModalUser(undefined);
  }, []);

  // 戻り値をメモ化して安定化
  return useMemo(
    () => ({
      // State
      isSearchModalOpen,
      isCreateChatModalOpen,
      selectedUserForChat,
      createModalUser,
      searchModalInitialMode,
      searchModalInitialParticipants,
      isUserLoading,

      // Handlers
      handleSendMessage,
      handleChatRoomSelect,
      handleCreateNewChat,
      handleCloseSearchModal,
      handleCloseCreateModal,

      // Utility
      createFallbackUser,
    }),
    [
      isSearchModalOpen,
      isCreateChatModalOpen,
      selectedUserForChat,
      createModalUser,
      searchModalInitialMode,
      searchModalInitialParticipants,
      isUserLoading,
      handleSendMessage,
      handleChatRoomSelect,
      handleCreateNewChat,
      handleCloseSearchModal,
      handleCloseCreateModal,
      createFallbackUser,
    ],
  );
};
